// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDesignerBody.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';
import m from 'mobx';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import DashboardSubscribe from 'modules/Dashboard/DashboardSubscribe/Izenda.DashboardSubscribe';
import ReportViewerFilters from 'modules/Reports/ReportViewer/Izenda.ReportViewerFilters';
import DashboardGrid from './DashboardGrid/Izenda.DashboardGrid';
import DashboardPresentation from './DashboardPresentation/Izenda.DashboardPresentation';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';
import DashboardFullscreen from './DashboardPresentation/Izenda.DashboardFullscreen';
import DashboardTile from './DashboardGrid/DashboardTile/Izenda.DashboardTile';
import CONSTANT from 'logic/data/Izenda.AccessConstants';
import AccessGrid from 'modules/Access/Izenda.AccessGrid';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
import { getOr, isNil } from 'lodash/fp';
import uiState from 'logic/Izenda.UiState';

const commonFilterAction = IntegrationStyleAction.checkDisplayByKey('hideCommonFilter');
const CommonFilter = commonFilterAction(ReportViewerFilters);

@connect(Dimensions)
class DashboardDesignerBody extends Component {
  static propTypes = {
    store: PropTypes.object
  };
  static displayName = 'DashboardDesignerBody';

  constructor() {
    super();

    this.reportViewerUIStore = { dashboardDesignerBodyHeight: this.dashboardDesignerBodyHeight };
  }

  /**
   * get the dashboard desginer body height
   * @return {number}
   */
  dashboardDesignerBodyHeight = (): number => {
    const dashboardDesignerBodyNode = ReactDOM.findDOMNode(this);
    const viewerContentNode = this.viewerContentNode;
    return getOr(0, 'clientHeight', dashboardDesignerBodyNode) - getOr(0, 'offsetTop', viewerContentNode);
  };

  /**
   * Handle on remove pill
   */
  onRemovingPillHandler = (...args) => {
    let store = this.props.store;
    return store.onRemovingPillHandler.call(store, ...args);
  };

  /**
   * Handle on remove all pill
   */
  onRemovingAllPillHandler = (...args) => {
    let store = this.props.store;
    return store.onRemovingAllPillHandler.call(store, ...args);
  };

  /**
   * render designer component
   * @return {Object} designer component
   */
  renderDesigner = () => {
    const props = this.props;
    const { store, setViewerContentRef, viewerContentHeight, updateViewerContentHeight } = props;
    let {
      dashboardDetail,
      dashboardDetail: {
        dashboard: { crossFilteringDetails, accessPriority }
      },
      dashboardFilterStore: {
        model: {
          reportFilter: { displayConfigFields: configFields }
        }
      }
    } = store;

    let contentContainerClassName = classnames('Izenda-DashboardGrid_Section');

    let dashboardDesignerContentClassName = classnames('izenda-DashboardDesignerContent', {
      u_none: !store.dashboardDesignerMode.isDesigner || !!dashboardDetail.dashboard.fullScreenDashboardPart
    });

    let dashboardDesignerContainerClassName = classnames('u_noMarginAll u_noPaddingAll u_fullHeight', {
      u_none: !store.dashboardDesignerMode.isDesigner
    });

    const setViewerContent = (node) => {
      this.viewerContentNode = node;
      setViewerContentRef(node);
    }

    const updateResults = () => {
      const props = this.props;
      const { loader, store } = props;
      const clonedModel = this.clonedModel || {};

      loader.startLoading();

      return store
        .validateDashboard(clonedModel)
        .then(function(result) {
          if (result.success) {
            return store.updateUseCache(true).then(() => store.updateResults());
          } else {
            if (result.messages[0].detail) {
              return props.confirmFn('Information', {
                description: DashboardConstants.validateFilterMessage(result),
                type: 'error',
                isHTMLFormat: true,
                showCloseIndicator: true
              });
            } else {
              return props.validation.updateErrors(result.messages);
            }
          }
        })
        .finally(loader.endLoading.bind(loader));
    };
    const doesDisableUpdateResultBtn =
      accessPriority === ACCESS_RIGHT.FullAccess ||
      accessPriority === ACCESS_RIGHT.SaveAs ||
      accessPriority === ACCESS_RIGHT.ViewOnly;

    const updateResultInfo = {
      updateResults: updateResults,
      doesDisableUpdateResultBtn: !doesDisableUpdateResultBtn,
      accessPriority
    };
    return (
      <div className={dashboardDesignerContainerClassName}>
        {dashboardDetail.dashboard.fullScreenDashboardPart && (
          <DashboardTile
            {...this.props}
            dashboardPart={dashboardDetail.dashboard.fullScreenDashboardPart}
            dashboardDetail={dashboardDetail}
            viewMode={false}
          />
        )}
        <div className={dashboardDesignerContentClassName}>
          <CommonFilter
            accessPriority={accessPriority}
            configFields={configFields}
            fieldStore={store.dashboardFilterStore}
            crossFilteringDetails={crossFilteringDetails}
            removeCrossFiltering={this.onRemovingPillHandler}
            removeAllCrossFiltering={this.onRemovingAllPillHandler}
            showCrossFilteringOption={false}
            hadSetting={false}
            enableDeleteAll
            isLazyLoading
            updateResultInfo={updateResultInfo}
            afterToggled={updateViewerContentHeight}
            afterMove={updateViewerContentHeight}
          />

          <DashboardGrid
            setViewerContentRef={setViewerContent}
            viewerContentHeight={viewerContentHeight}
            className={contentContainerClassName}
            {...this.props}
            dashboardDetail={dashboardDetail}
            reportViewerUIStore={this.reportViewerUIStore}
          />
        </div>
      </div>
    );
  };

  /**
   * render presentation component
   * @return {Object} presentation component
   */
  renderPresentation = () => {
    return this.props.store.dashboardDesignerMode.isPresentation ? <DashboardPresentation {...this.props} /> : null;
  };

  /**
   * render presentation component
   * @return {Object} presentation component
   */
  renderFullscreen = () => {
    return this.props.store.dashboardDesignerMode.isFullscreen ? <DashboardFullscreen {...this.props} /> : null;
  };

  /**
   * render subscribe component
   * @return {Object} subscribe component
   */
  renderSubscribe = () => {
    return this.props.store.dashboardDesignerMode.isSubscribe ? <DashboardSubscribe {...this.props} /> : null;
  };

  /**
   * render scheduling component
   * @return {Object} scheduling component
   */
  renderScheduling = () => {
    return this.props.store.dashboardDesignerMode.isSchedule ? <DashboardSubscribe {...this.props} /> : null;
  };

  /**
   * render access component
   * @return {Object} access component
   */
  renderAccess = () => {
    const store = this.props.store;
    const isGlobalTenant = !isNil(uiState.tenantId) && getOr(false, 'dashboardDetail.dashboard.isGlobal', store);

    return store.dashboardDesignerMode.isAccess ? (
      <AccessGrid
        isDashboard
        dashboardId={store.dashboardDetail.dashboard.id}
        isGlobal={store.dashboardDetail.dashboard.isGlobal}
        readOnly={isGlobalTenant}
        store={store.accessStore}
        users={store.accessStore.users}
        roles={store.accessStore.roles}
        tableDef={CONSTANT.ACCESS_TABLE_DEF}
        searchOptions={CONSTANT.SEARCH_OPTIONS}
        accessMode={CONSTANT.ACCESS_MODE.DASHBOARD}
      />
    ) : null;
  };

  render() {
    return (
      <div className='izenda-DashboardDesignerBody u_fullHeight u_marginTop'>
        {this.renderDesigner()}
        {this.renderPresentation()}
        {this.renderSubscribe()}
        {this.renderScheduling()}
        {this.renderFullscreen()}
        {this.renderAccess()}
      </div>
    );
  }
}

export default DashboardDesignerBody;



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/Izenda.DashboardDesignerBody.js